import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Navigation.css';

const Navigation = ({
  activeTab = 'facilities',
  setActiveTab = () => {},
  isAdmin = false,
  isPresident = false,
  isVicePresident = false,
  isSecretary = false,
  isTreasurer = false,
  pendingCount = 0,
  facilitiesCount = 0,
  sectionsCount = 0,
  myBookingsCount = 0
}) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const navRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const isLeadership = isPresident || isVicePresident || isSecretary || isTreasurer || isAdmin;

  const navItems = [
    { id: 'facilities', label: 'Facilities', icon: 'fa-solid fa-building', count: facilitiesCount },
    { id: 'sections', label: 'Sections', icon: 'fa-solid fa-layer-group', count: sectionsCount },
    { id: 'join-section', label: 'Join Section', icon: 'fa-solid fa-user-plus', count: null },
    { id: 'equipment', label: 'Borrow Equipment', icon: 'fa-solid fa-toolbox', count: null },
    ...(isLeadership ? [{ id: 'admin', label: 'Admin', icon: 'fa-solid fa-shield', count: pendingCount }] : []),
    { id: 'my-bookings', label: 'My Bookings', icon: 'fa-solid fa-calendar', count: myBookingsCount }
  ];

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 20);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 20);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const targetScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      checkScrollButtons();
    };
    
    window.addEventListener('resize', handleResize);
    checkScrollButtons();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [navItems]);

  useEffect(() => {
    // Check scroll buttons after render and when activeTab changes
    setTimeout(checkScrollButtons, 100);
  }, [activeTab]);

  return (
    <div className="navigation-wrapper" ref={navRef}>
      <AnimatePresence>
        {showLeftArrow && (
          <motion.button 
            className="nav-scroll-btn nav-scroll-left"
            onClick={() => scroll('left')}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            whileHover={{ scale: 1.1 }}
          >
            <i className="fas fa-chevron-left"></i>
          </motion.button>
        )}
      </AnimatePresence>

      <div className="booking-nav" ref={scrollContainerRef}>
        {navItems.map((item) => (
          <motion.button 
            key={item.id}
            className={`nav-btn ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => setActiveTab(item.id)}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <i className={item.icon}></i>
            <span className="nav-label">
              {item.label}
              {item.count !== null && item.count > 0 && (
                <span className="nav-badge">{item.count}</span>
              )}
            </span>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {showRightArrow && (
          <motion.button 
            className="nav-scroll-btn nav-scroll-right"
            onClick={() => scroll('right')}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            whileHover={{ scale: 1.1 }}
          >
            <i className="fas fa-chevron-right"></i>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Navigation;